import { Box, Typography } from '@mui/material'
import {
  DataGridPro,
  GridColDef,
  GridEventListener,
  GridFooter,
  GridFooterContainer,
  GridRowsProp,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValueFormatterParams,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../models'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import {
  useLazyListVehicleWithCustomersQuery,
  useListVehicleWithCustomersQuery
} from '../../redux/api/vehicleApi'
import { CustomerEntry, VehicleWithCustomers } from '../../sdk'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import formatMdyDate from '../../utils/formatMdyDate/formatMdyDate'
import { SearchWithAutocomplete } from '../SearchWithAutocomplete/SearchWithAutocomplete'

// TODO: check why some values are dropped when page_size is too small e.g. 10
const PAGE_SIZE = 1000

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <div style={{ flexGrow: 1 }} />
      <SearchWithAutocomplete />
    </GridToolbarContainer>
  )
}

const CustomFooter = ({ reachedEnd }: { reachedEnd: boolean }) => {
  return (
    <GridFooterContainer>
      <Box pl={2}>
        {reachedEnd ? (
          <Typography>All Results Loaded</Typography>
        ) : (
          <Typography>Loading Remaining Rows...</Typography>
        )}
      </Box>
      <div style={{ flexGrow: 1 }} />
      <GridFooter />
    </GridFooterContainer>
  )
}

const CustomFooterAdd = () => {
  return <CustomFooter reachedEnd={false} />
}

const CustomFooterAll = () => {
  return <CustomFooter reachedEnd={true} />
}

const VehicleList: FC = () => {
  const navigate = useNavigate()
  const gridApiRef = useGridApiRef()

  const { gridBoxHeight } = useContext(AppContext)

  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [triggerSearch] = useLazyListVehicleWithCustomersQuery()

  // fetch initial entries and display first
  const { data: initial, isLoading } = useListVehicleWithCustomersQuery({
    authorization: accessToken,
    shop: shopId,
    maxTimestampExclusive: '9999-12-31T23:59:59+00',
    numResults: PAGE_SIZE
  })

  // slice to solve vehicleEntry with multiple customerEntry issue
  // see rows for more info
  const initialVehicleWithCustomersList = useMemo(
    () => initial?.slice(0, initial.length - 1) || [],
    [initial]
  )

  const [
    remainingVehicleWithCustomersList,
    setRemainingVehicleWithCustomersList
  ] = useState<VehicleWithCustomers[]>([])

  useEffect(() => {
    const fetchData = async () => {
      // only triggered when initialVehicleWithCustomersList is changed
      if (initialVehicleWithCustomersList.length > 0) {
        let remaining: VehicleWithCustomers[] = []

        // fetch remaining entries and add only after all fetched in background
        while (true) {
          // note that initialVehicleWithCustomersList length is at least 1 here
          const endDateTime =
            remaining.length > 0
              ? remaining[
                  remaining.length - 1
                ].vehicleEntry?.created!.toISOString()
              : initialVehicleWithCustomersList[
                  initialVehicleWithCustomersList.length - 1
                ].vehicleEntry?.created!.toISOString()

          const next = await triggerSearch({
            authorization: accessToken,
            shop: shopId,
            maxTimestampExclusive: endDateTime,
            numResults: PAGE_SIZE
          }).unwrap()

          if (next.length >= 2) {
            // slice to solve vehicleEntry with multiple customerEntry issue
            // see rows for more info
            remaining = [...remaining, ...next.slice(0, next.length - 1)]
          } else {
            remaining = [...remaining, ...next]

            break
          }
        }

        setRemainingVehicleWithCustomersList(remaining)
      }
    }

    fetchData()
  }, [accessToken, initialVehicleWithCustomersList, shopId, triggerSearch])

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'vin',
        headerName: 'VIN',
        flex: 1
      },
      {
        field: 'licensePlate',
        headerName: 'License Plate',
        flex: 1
      },
      {
        field: 'registeredState',
        headerName: 'State',
        flex: 1
      },
      {
        field: 'year',
        headerName: 'Year',
        flex: 1
      },
      {
        field: 'make',
        headerName: 'Make',
        flex: 1
      },
      {
        field: 'model',
        headerName: 'Model',
        flex: 1
      },
      {
        field: 'submodel',
        headerName: 'Submodel',
        flex: 1
      },
      {
        field: 'engine',
        headerName: 'Engine',
        flex: 1
      },
      {
        field: 'created',
        headerName: 'Created Date',
        flex: 1,
        type: 'date',
        valueFormatter: (params: GridValueFormatterParams) =>
          formatMdyDate(params.value as Date)
      },
      {
        field: 'customers',
        headerName: 'Customers',
        flex: 2
      }
    ]
  }, [])

  const allVehicleWithCustomers = useMemo(() => {
    // ensure that final list is unique
    // fetch goes by number of rows and for one vehicleEntry it can have multiple rows because of multiple customerEntry
    // if fetch cuts off the remaining customerEntry for one vehicleEntry, that vehicleWithCustomers would be refetched in the next call
    // need to update to use the latest vehicleWithCustomers and remove duplicates
    const map = new Map<string, VehicleWithCustomers>()
    for (const item of initialVehicleWithCustomersList) {
      map.set(item.vehicleEntry!.id!, item)
    }
    for (const item of remainingVehicleWithCustomersList) {
      map.set(item.vehicleEntry!.id!, item)
    }
    return Array.from(map.values())
  }, [initialVehicleWithCustomersList, remainingVehicleWithCustomersList])

  const rows: GridRowsProp = useMemo(() => {
    return (allVehicleWithCustomers || []).map((vc) => ({
      id: vc.vehicleEntry?.id,
      ...vc.vehicleEntry?.info,
      year: vc.vehicleEntry?.info?.year,
      make: vc.vehicleEntry?.info?.make?.name,
      model: vc.vehicleEntry?.info?.model?.name,
      submodel: vc.vehicleEntry?.info?.subModel?.name,
      engine: vc.vehicleEntry?.info?.engine?.name,
      created: vc.vehicleEntry?.created,
      customers: vc.customerEntries
        ?.map((customerEntry: CustomerEntry) =>
          formatName(customerEntry.information)
        )
        .join(', ')
    }))
  }, [allVehicleWithCustomers])

  useEffect(() => {
    const onRowClick: GridEventListener<'rowClick'> = ({ id }) => {
      const vehicleUrl = generatePath(AppRoutes.VEHICLE_DETAILS, {
        id: id as string
      })
      navigate(vehicleUrl)
    }

    return gridApiRef.current.subscribeEvent('rowClick', onRowClick)
  }, [gridApiRef, navigate])

  return (
    <Box sx={{ height: gridBoxHeight, width: '100%' }}>
      <DataGridPro
        apiRef={gridApiRef}
        density="compact"
        rows={rows}
        columns={columns}
        loading={isLoading}
        slots={{
          toolbar: CustomToolbar,
          footer:
            remainingVehicleWithCustomersList.length >= 1
              ? CustomFooterAll
              : CustomFooterAdd
        }}
        sx={{
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer'
          },
          border: '0px'
        }}
      />
    </Box>
  )
}

export default VehicleList
